import React from "react";
import { useTranslation } from "react-i18next";
import {
  useTaskStatusesRetrieval,
  useRemoveTaskStatus,
} from "@planzone/view-models";
import { Identifiable, Status as S } from "@planzone/dto";
import { Button } from "@common/button";
import { Modal } from "@common/modal";
import { Status } from "./status";
import { Props } from "./types";
import { TransferIcon } from "./transferIcon";
import { StatusSelect } from "./statusSelect";
import styles from "./styles.scss";

function RemoveTaskStatus({ project, status, cancel, hasNoTasks }: Props) {
  const { t } = useTranslation();
  const { isRequestPending, removeTaskStatus } = useRemoveTaskStatus();
  const [destinationStatus, setDestinationStatus] =
    React.useState<Identifiable<S> | null>(null);
  const { statuses, retrieveTaskStatuses } = useTaskStatusesRetrieval(project);

  React.useEffect(() => {
    retrieveTaskStatuses(project);
  }, [project]);

  return (
    <Modal
      header={t("removeTaskStatus.title")}
      headerClassName={styles.header}
      close={cancel}
    >
      <div className={styles.main}>
        <div className={styles.content}>
          <div className={styles.group}>
            {hasNoTasks ? (
              <p className={styles.warning}>
                {t("removeTaskStatus.warningNoTasks")}
              </p>
            ) : (
              <p className={styles.warning}>
                {t("removeTaskStatus.warningTasks")}
              </p>
            )}
          </div>
          {hasNoTasks ? (
            <div className={styles.status}>
              <Status status={status} />
            </div>
          ) : (
            <div className={styles.group}>
              <Status status={status} />
              <TransferIcon className={styles.transferIcon} />
              <StatusSelect
                selectedStatus={destinationStatus}
                statuses={statuses.filter(({ uuid }) => status.uuid !== uuid)}
                placeholder={t("removeTaskStatus.placeholder")}
                selectStatus={setDestinationStatus}
              />
            </div>
          )}
        </div>
        <div className={styles.buttons}>
          <Button type="cancel" press={cancel}>
            {t("removeTaskStatus.cancel")}
          </Button>
          <Button
            type="delete"
            status={isRequestPending ? "pending" : "none"}
            press={() =>
              !isRequestPending &&
              (destinationStatus
                ? removeTaskStatus({
                    nextStatus: destinationStatus,
                    status,
                  })
                : removeTaskStatus({
                    nextStatus: status,
                    status,
                  }))
            }
          >
            {hasNoTasks
              ? t("removeTaskStatus.confirmNoTasks")
              : t("removeTaskStatus.confirmTasks")}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export { RemoveTaskStatus };
